import type { JSX } from 'react';
import { Box, TextField, Button, Alert, CircularProgress, FormHelperText } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SendIcon from '@mui/icons-material/Send';
import SaveIcon from '@mui/icons-material/Save';
import LoginIcon from '@mui/icons-material/Login';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import RefreshIcon from '@mui/icons-material/Refresh';
import { formatTime } from '@footsteps/shared';
import { RegisterUserProvider, useRegisterUser } from './register-user.provider';
import type { RegisterUserProps } from './register-user.types';
import { styles } from './register-user.styles';
import { OtpInput } from './OtpInput';

const LoginForm = (): JSX.Element => {
  const { state, viewModel } = useRegisterUser();

  return (
    <Box
      component='form'
      autoComplete='on'
      onSubmit={(e) => {
        e.preventDefault();
        viewModel.submitLoginEmail();
      }}
      sx={styles.form}
    >
      <p className='text-body text-sm' style={{ marginBottom: 4 }}>
        Voer je e-mailadres in. <br /> We sturen een 6-cijferige inlogcode naar je e-mail.
      </p>
      <div className='form-row'>
        <label className='form-label'>E-mailadres</label>
        <TextField
          name='email'
          type='email'
          placeholder='naam@voorbeeld.nl'
          value={state.email}
          onChange={(e) => viewModel.setEmail(e.target.value)}
          error={!!state.errors.email}
          helperText={state.errors.email}
          autoComplete='email'
        />
      </div>
      {state.apiError && (
        <Alert severity='error' sx={styles.alert}>
          {state.apiError}
        </Alert>
      )}
      <div className='flex flex-wrap gap-3 justify-end mt-1'>
        <Button variant='outlined' size='small' onClick={viewModel.switchToRegister}>
          Mijn gegevens zijn niet bekend
        </Button>
        <Button
          type='submit'
          variant='contained'
          size='small'
          disabled={state.isLoading}
          startIcon={state.isLoading ? <CircularProgress size={16} color='inherit' /> : <SendIcon />}
        >
          {state.isLoading ? 'Bezig...' : 'Stuur inlogcode'}
        </Button>
      </div>
    </Box>
  );
};

const RegisterForm = (): JSX.Element => {
  const { state, viewModel } = useRegisterUser();

  return (
    <Box
      component='form'
      autoComplete='on'
      onSubmit={(e) => {
        e.preventDefault();
        viewModel.submitRegister();
      }}
      sx={styles.form}
    >
      {state.isEmailEditable ? (
        <div className='form-row'>
          <label className='form-label'>E-mailadres</label>
          <TextField
            name='email'
            type='email'
            placeholder='naam@voorbeeld.nl'
            value={state.email}
            onChange={(e) => viewModel.setEmail(e.target.value)}
            error={!!state.errors.email}
            helperText={state.errors.email}
            autoComplete='email'
          />
        </div>
      ) : (
        <div className='info-row'>
          <span className='text-muted'>E-mailadres</span>
          <span className='text-value'>{state.email}</span>
        </div>
      )}
      <div className='form-row'>
        <label className='form-label'>Volledige naam</label>
        <TextField
          name='name'
          placeholder='Jan Jansen'
          value={state.fullName}
          onChange={(e) => viewModel.setFullName(e.target.value)}
          error={!!state.errors.fullName}
          helperText={state.errors.fullName}
          autoComplete='name'
        />
      </div>
      <div className='form-row'>
        <label className='form-label'>Telefoonnummer</label>
        <TextField
          name='tel'
          type='tel'
          placeholder='06 12345678 (optioneel)'
          value={state.telephone}
          onChange={(e) => viewModel.setTelephone(e.target.value)}
          error={!!state.errors.telephone}
          helperText={state.errors.telephone}
          autoComplete='tel'
        />
      </div>
      {state.apiError && (
        <Alert severity='error' sx={styles.alert}>
          {state.apiError}
        </Alert>
      )}
      <div className='flex flex-wrap gap-3 justify-end mt-1'>
        {state.isEmailEditable && (
          <Button variant='outlined' size='small' onClick={viewModel.switchToLogin}>
            Mijn gegevens zijn bekend
          </Button>
        )}
        <Button
          type='submit'
          variant='contained'
          size='small'
          disabled={state.isLoading}
          startIcon={state.isLoading ? <CircularProgress size={16} color='inherit' /> : <SaveIcon />}
        >
          {state.isLoading ? 'Bezig...' : state.isEmailEditable ? 'Opslaan en verifiëren' : 'Opslaan'}
        </Button>
      </div>
    </Box>
  );
};

const LoginOtpStep = (): JSX.Element => {
  const { state, viewModel } = useRegisterUser();

  return (
    <Box
      component='form'
      autoComplete='on'
      onSubmit={(e) => {
        e.preventDefault();
        viewModel.submitLoginOtp();
      }}
      sx={styles.form}
    >
      <p className='text-body text-sm' style={{ marginBottom: 4 }}>
        We hebben een 6-cijferige code gestuurd naar <strong>{state.email}</strong>.<br /> Voer de code hieronder in.
      </p>
      <div className='form-row'>
        <label className='form-label'>Verificatiecode</label>
        <div>
          <OtpInput
            value={state.otp}
            onChange={viewModel.setOtp}
            hasError={!!state.errors.otp}
            onComplete={() => viewModel.submitLoginOtp()}
          />
          {state.errors.otp && <FormHelperText error>{state.errors.otp}</FormHelperText>}
        </div>
      </div>
      {state.apiError && (
        <Alert severity='error' sx={styles.alert}>
          {state.apiError}
        </Alert>
      )}
      <div className='flex flex-wrap gap-3 items-center justify-end mt-1'>
        <Button
          type='submit'
          variant='contained'
          size='small'
          disabled={state.isLoading || state.otp.length !== 6}
          startIcon={state.isLoading ? <CircularProgress size={16} color='inherit' /> : <LoginIcon />}
        >
          {state.isLoading ? 'Bezig...' : 'Inloggen'}
        </Button>
        <Button
          variant='text'
          size='small'
          startIcon={<RefreshIcon />}
          onClick={viewModel.resendLoginOtp}
          disabled={state.resendCooldown > 0 || state.isLoading}
        >
          {state.resendCooldown > 0
            ? `Opnieuw verzenden (${formatTime(state.otpTimeRemaining)})`
            : 'Code opnieuw verzenden'}
        </Button>
      </div>
    </Box>
  );
};

const OtpForm = (): JSX.Element => {
  const { state, viewModel } = useRegisterUser();

  return (
    <Box
      component='form'
      autoComplete='on'
      onSubmit={(e) => {
        e.preventDefault();
        viewModel.submitOtp();
      }}
      sx={styles.form}
    >
      <p className='text-body text-sm' style={{ marginBottom: 4 }}>
        We hebben een 6-cijferige verificatiecode gestuurd naar <strong>{state.email}</strong>.<br /> Voer de code
        hieronder in om je e-mailadres te bevestigen.
      </p>
      <div className='form-row'>
        <label className='form-label'>Verificatiecode</label>
        <div>
          <OtpInput
            value={state.otp}
            onChange={viewModel.setOtp}
            hasError={!!state.errors.otp}
            onComplete={() => viewModel.submitOtp()}
          />
          {state.errors.otp && <FormHelperText error>{state.errors.otp}</FormHelperText>}
        </div>
      </div>
      {state.apiError && (
        <Alert severity='error' sx={styles.alert}>
          {state.apiError}
        </Alert>
      )}
      <div className='flex flex-wrap gap-3 items-center justify-end mt-1'>
        <Button
          type='submit'
          variant='contained'
          size='small'
          disabled={state.isLoading || state.otp.length !== 6}
          startIcon={state.isLoading ? <CircularProgress size={16} color='inherit' /> : <VerifiedUserIcon />}
        >
          {state.isLoading ? 'Bezig...' : 'Verifiëren'}
        </Button>
        <Button
          variant='text'
          size='small'
          startIcon={<RefreshIcon />}
          onClick={viewModel.resendOtp}
          disabled={state.resendCooldown > 0 || state.isLoading}
        >
          {state.resendCooldown > 0
            ? `Opnieuw verzenden (${formatTime(state.otpTimeRemaining)})`
            : 'Code opnieuw verzenden'}
        </Button>
      </div>
    </Box>
  );
};

const VerifyStep = (): JSX.Element => {
  return <OtpForm />;
};

const VerifiedStep = (): JSX.Element => {
  return (
    <div className='flex flex-col items-center gap-4 py-4'>
      <CheckCircleOutlineIcon color='success' sx={{ fontSize: 48 }} />
      <p className='text-body text-sm text-center'>Je e-mailadres is succesvol geverifieerd!</p>
    </div>
  );
};

const RegisterUserContent = (): JSX.Element => {
  const { state } = useRegisterUser();

  switch (state.step) {
    case 'login':
      return <LoginForm />;
    case 'register':
      return <RegisterForm />;
    case 'login-otp':
      return <LoginOtpStep />;
    case 'verify':
      return <VerifyStep />;
    case 'verified':
      return <VerifiedStep />;
  }
};

export const RegisterUser = (props: RegisterUserProps): JSX.Element => {
  return (
    <RegisterUserProvider {...props}>
      <RegisterUserContent />
    </RegisterUserProvider>
  );
};
